import { EyeIcon } from 'lucide-react';

import type { OutlineEntry } from '../types';

import { Button } from '@/components/ui/button';

interface ReportsSlidesPanelProps {
  entries: OutlineEntry[];
  isFinalPreview: boolean;
  onSelectFinalPreview: () => void;
  onSelectPage: (pageNumber: number) => void;
  page: number;
}

const getSeparatorLabel = (title: string): null | string => {
  const normalized = title.trim();
  if (/^general appendices$/i.test(normalized)) {
    return 'General Appendices';
  }
  if (/^(icb|icm|rsm|ficm)\s+appendices$/i.test(normalized)) {
    return 'Company Appendices';
  }
  return null;
};

const ReportsSlidesPanel = ({
  entries,
  isFinalPreview,
  onSelectFinalPreview,
  onSelectPage,
  page
}: ReportsSlidesPanelProps) => {
  return (
    <div className="bg-muted/20 flex w-56 shrink-0 flex-col border-r">
      <div className="px-3 py-2 text-sm font-semibold text-muted-foreground">Slides</div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {entries.length ? (
          <div className="space-y-1">
            {entries.map((entry, index) => {
              const separatorLabel = getSeparatorLabel(entry.title);
              if (separatorLabel) {
                return (
                  <div
                    className="text-muted-foreground mt-3 border-t px-2 pt-2 text-sm font-medium"
                    key={`separator-${separatorLabel}-${String(entry.pageNumber)}`}
                  >
                    {separatorLabel}
                  </div>
                );
              }

              return (
                <Button
                  className="h-auto w-full justify-start px-2 py-1.5 text-left font-normal"
                  key={`${entry.title}-${String(entry.pageNumber)}`}
                  onClick={() => {
                    onSelectPage(entry.pageNumber);
                  }}
                  title={entry.title}
                  variant={
                    page >= entry.pageNumber &&
                    page <
                      (entries[index + 1]?.pageNumber ?? Number.POSITIVE_INFINITY)
                      ? 'secondary'
                      : 'ghost'
                  }
                >
                  <span className="block w-full truncate">{entry.title}</span>
                </Button>
              );
            })}
          </div>
        ) : (
          <div className="text-muted-foreground px-2 py-1 text-sm">
            No slide titles available
          </div>
        )}
      </div>
      <div className="border-border border-t p-2">
        <Button
          className="h-auto w-full justify-start px-2 py-1.5 text-left font-normal"
          onClick={onSelectFinalPreview}
          variant={isFinalPreview ? 'secondary' : 'ghost'}
        >
          <EyeIcon className="mr-2 size-4" />
          <span className="block w-full truncate">Final Preview</span>
        </Button>
      </div>
    </div>
  );
};

export default ReportsSlidesPanel;
